import {
  CHART_SERIES,
  ActionTile,
  ActionTileGrid,
  ActivityRow,
  AttentionRow,
  Card,
  CountRow,
  DashboardBand,
  ChartUnavailable,
  DonutWithLegend,
  EmptyState,
  ExecutiveBand,
  ExecutiveCard,
  LoadingState,
  MiniBarChart,
  RowList,
  SectionHeader,
  SnapshotCard,
  type AccentTone,
  type BadgeTone,
  type DonutSegment,
  type MiniBarGroup,
  type SegmentTone,
  type SnapshotStat,
  type IconName,
} from '@tessma/design-system';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Refusal, useAsync } from '../ui/async.js';
import { useShellBridge } from '../shell/shell-bridge.js';
import type { MetricCard } from '../analytics/gateway.js';
import type { Dashboard, DashboardGateway, ModuleSnapshot } from './gateway.js';

/**
 * SCREEN 00 — the Main Dashboard, against its approved reference.
 *
 * The tenant-wide, cross-module business command centre. It is NOT the Finance
 * overview (`/finance`) and NOT Administration: those answer "how is this
 * module doing" and "how is this tenant configured", and this one answers
 * "what should I do today".
 *
 * **EVERY SECTION IS COMPOSED BY THE SERVER.** This file renders the answer to
 * `GET /api/v1/dashboard` and makes no decision about what anybody may see. A
 * section that is null did not come back, and a section that did not come back
 * is one this caller has no claim to — so there is nothing here to filter and
 * nothing a devtools panel could un-hide.
 *
 * **THE ANATOMY IS THE REFERENCE'S**, and it is the reason the first attempt
 * failed acceptance. The reference does not draw cards with text in them: it
 * draws tinted icon badges, coloured deltas with arrows, miniature charts, a
 * donut with its total in the middle, rows with badges and module chips and
 * ages, and quick actions as a grid of tiles. That anatomy now lives in
 * `@tessma/design-system/dashboard` so every screen using it gets the same one.
 *
 * **THE CONTENT IS NOT THE REFERENCE'S.** Its revenue, headcount, pipeline and
 * compliance figures are a mockup's, and seven of the eight modules it draws
 * have no tables behind them. Nothing here invents one to fill a card.
 */

/**
 * WHICH BADGE AND HUE A FIGURE WEARS. Presentation, and only presentation: the
 * key, the title, the unit and the permission are all the server's, and this
 * decides nothing except what the reader sees beside them. A metric with no
 * entry here still renders — in the default hue — rather than disappearing,
 * because a missing icon must never cost somebody a figure.
 */
const METRIC_STYLE: Record<string, { readonly icon: IconName; readonly tone: AccentTone }> = {
  'platform.open_tasks': { icon: 'tray', tone: 'info' },
  'platform.tasks_by_priority': { icon: 'list', tone: 'warning' },
  'platform.tenant_task_total': { icon: 'squares-four', tone: 'teal' },
  'finance.outstanding_receivables': { icon: 'bank', tone: 'success' },
  'finance.invoices_by_status': { icon: 'tray', tone: 'info' },
  'finance.outstanding_payables': { icon: 'arrows-down-up', tone: 'danger' },
  'finance.bills_awaiting_approval': { icon: 'check-circle', tone: 'warning' },
  'finance.revenue': { icon: 'trend-up', tone: 'success' },
  'finance.expenses': { icon: 'trend-down', tone: 'danger' },
  'finance.net_profit': { icon: 'chart-bar', tone: 'info' },
};

const DEFAULT_STYLE = { icon: 'chart-bar', tone: 'teal' } as const;

function styleFor(key: string): { readonly icon: IconName; readonly tone: AccentTone } {
  return METRIC_STYLE[key] ?? DEFAULT_STYLE;
}

const NUMBER = new Intl.NumberFormat('en-GB');
const MONEY = new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2 });

/**
 * A FIGURE IN THE UNIT THE SERVER DECLARED IT IN.
 *
 * `currency` means MINOR UNITS — the ledger is `numeric(38, 0)` in pence for
 * the reason floats are not money — so it is divided here and only here. This
 * read `card.unit === 'currency_minor'`, which is not a unit the platform has
 * ever declared, so every currency figure on this screen rendered as its own
 * pence count: £33,864.00 of receivables appeared as "3,386,400". Every test
 * passed, because the number was the server's number.
 *
 * NO SYMBOL, because no symbol has been earned. A metric card carries no
 * currency code — a tenant may hold entities in several — and a "£" this file
 * decided on would be a claim about which currency the figure is in that
 * nothing behind it supports.
 */
function cardValue(card: MetricCard): string | undefined {
  if (card.unavailable !== undefined || card.total === null) return undefined;
  return card.unit === 'currency' ? MONEY.format(card.total / 100) : NUMBER.format(card.total);
}

/**
 * The delta beside each headline figure.
 *
 * THE ONE ARITHMETIC THIS SCREEN DOES, and it states no new fact: `total` and
 * `previousTotal` are both the platform's, both already scoped to this caller,
 * so the percentage is a presentation of one answer rather than a second
 * opinion on it.
 */
function changePercent(card: MetricCard): number | undefined {
  const { total, previousTotal } = card;
  if (total === null || previousTotal === null || previousTotal === 0) return undefined;
  return ((total - previousTotal) / Math.abs(previousTotal)) * 100;
}

/**
 * The donut's segments, from the figures the server already sent.
 *
 * A DIMENSIONED METRIC IS ALREADY A BREAKDOWN — `finance.invoices_by_status`
 * carries one point per status — so the donut is a drawing of the server's own
 * answer rather than a client-side aggregation of rows.
 */
const SEGMENT_TONE: Record<string, SegmentTone> = {
  paid: 'success',
  settled: 'success',
  issued: 'info',
  sent: 'info',
  overdue: 'danger',
  disputed: 'danger',
  draft: 'neutral',
  void: 'neutral',
};

function segmentsOf(card: MetricCard): readonly DonutSegment[] {
  const byDimension = new Map<string, number>();
  for (const point of card.points) {
    if (point.dimensionKey === '') continue;
    byDimension.set(point.dimensionKey, (byDimension.get(point.dimensionKey) ?? 0) + point.value);
  }
  /*
   * SEMANTIC WHERE IT MEANS SOMETHING, categorical where it does not.
   *
   * Paid really is green and overdue really is red, so those carry the frozen
   * semantic tones. A dimension with no such mapping — a department, an
   * account — gets the v1.2 chart palette instead: reaching for `danger` there
   * would invite a reader to see a problem nobody reported. Unmapped segments
   * cycle the four in order, so the same series is the same colour on every
   * render rather than shifting when a value changes.
   */
  let nextSeries = 0;
  return [...byDimension]
    .filter(([, value]) => value > 0)
    .map(([dimensionKey, value]) => {
      const semantic = SEGMENT_TONE[dimensionKey.toLowerCase()];
      const tone = semantic ?? CHART_SERIES[nextSeries++ % CHART_SERIES.length] ?? 'neutral';
      return {
        label: dimensionKey.charAt(0).toUpperCase() + dimensionKey.slice(1).replaceAll('_', ' '),
        value,
        tone,
      };
    });
}

const MONTH_ONLY = new Intl.DateTimeFormat('en-GB', { month: 'short', timeZone: 'UTC' });

/**
 * "vs. Aug", not "vs. the previous period" and not "vs. Aug 2026".
 *
 * The long form ellipsised beside the sparkline, so a card read "vs. previ…" —
 * naming no period at all, which is worse than naming none. The server now
 * says which window it compared against, so the card names it.
 *
 * WITHOUT THE YEAR, because the comparison is always the window IMMEDIATELY
 * before this one: the year is never in doubt and the two words it costs are
 * the difference between a caption and an ellipsis. The reference carries
 * "vs. Mar 2025" in a card a third wider than ours.
 */
function comparisonCaption(comparedTo: { readonly from: string; readonly to: string }): string {
  const parsed = new Date(`${comparedTo.to}T00:00:00.000Z`);
  return Number.isNaN(parsed.getTime()) ? 'vs. previous' : `vs. ${MONTH_ONLY.format(parsed)}`;
}

/**
 * The designer's card tones are the five semantic ones; `accent` is the
 * product's sixth and has no tinted surface behind it, so it reads as teal.
 */
function executiveTone(tone: AccentTone): BadgeTone | 'teal' {
  return tone === 'accent' ? 'teal' : tone;
}

/**
 * ONE EXECUTIVE CARD — `components/ExecutiveSnapshot.tsx`, bound to the
 * server's own figures.
 *
 * EVERYTHING ON IT IS THE SERVER'S. Their six cards are fixtures with a
 * hand-drawn polyline each; these carry the metric's real `points`, a trend
 * taken from `total` against `previousTotal`, and a caption naming the window
 * the dashboard endpoint says it compared against. A card with no previous
 * figure shows no trend rather than an invented one.
 */
function Kpi({
  card,
  comparedTo,
}: {
  readonly card: MetricCard;
  readonly comparedTo: { readonly from: string; readonly to: string };
}) {
  const { icon, tone } = styleFor(card.key);
  const segments = segmentsOf(card);
  const value = cardValue(card);
  const change = changePercent(card);

  /*
   * THE SERIES, IN THE DESIGNER'S OWN SHAPE: one number per period, in order.
   * With a single period there is nothing to trend, so the pair the delta is a
   * percentage of is plotted instead — two figures the server measured for two
   * windows. With neither, nothing is drawn: a two-point line through invented
   * data is a trend nobody measured.
   */
  const series: readonly number[] | undefined =
    card.points.length > 1
      ? card.points.map((point) => point.value)
      : card.previousTotal !== null && card.total !== null
        ? [card.previousTotal, card.total]
        : undefined;

  return (
    <ExecutiveCard
      icon={icon}
      tone={executiveTone(tone)}
      label={card.title}
      value={value ?? null}
      {...(card.unavailable === undefined ? {} : { unavailable: card.unavailable.detail })}
      {...(change === undefined
        ? {}
        : {
            change: `${String(Math.abs(Math.round(change)))}%`,
            changeDirection: change < 0 ? ('down' as const) : ('up' as const),
            caption: comparisonCaption(comparedTo),
          })}
      {...(segments.length > 1
        ? {
            /*
             * A BREAKDOWN IS NOT A SERIES. `platform.tasks_by_priority` carries
             * one figure per priority, and the designer's six cards are all
             * series, so they draw no card for this: a dimensioned metric
             * plotted as a line would be three priorities drawn as one trend.
             *
             * THE LEGEND, NOT THE RING, and the capture harness is why. Six of
             * these sit in a row 145 pixels tall; a ring is 72 of them before
             * its own keys, and the first capture of this screen showed the arc
             * cut through by the card's lower edge. Every figure the ring would
             * have drawn is in the legend, in words.
             */
            segments,
          }
        : series === undefined
          ? {}
          : { points: series })}
    />
  );
}

/**
 * `platform.task.created` → `Task created`.
 *
 * DERIVED FROM THE KEY, never from a table of phrases. The activity type is a
 * dotted key whose last two segments are the thing and what happened to it, so
 * the line a reader gets is the server's own vocabulary made readable rather
 * than a second vocabulary that has to be kept in step with it. An unfamiliar
 * key still renders — as itself — because a line nobody mapped must not become
 * a blank row.
 */
function activityLabel(activityType: string): string {
  const parts = activityType.split('.').filter(Boolean);
  const verb = parts.at(-1);
  const subject = parts.at(-2);
  if (verb === undefined || subject === undefined) return activityType;
  const humanise = (word: string) => word.split(/[-_]/).join(' ');
  const noun = humanise(subject);
  return `${noun.charAt(0).toUpperCase()}${noun.slice(1)} ${humanise(verb)}`;
}

/** `finance.invoice` → `Finance`, from the key the server already sent. */
function moduleOf(resource: string): string {
  const [head] = resource.split('.');
  if (head === undefined || head === '') return resource;
  if (head === 'platform') return 'Platform';
  return head
    .split(/[-_]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function daysSince(iso: string | null): number | null {
  if (iso === null) return null;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return null;
  return Math.floor((Date.now() - parsed.getTime()) / 86_400_000);
}

function ageLabel(iso: string | null): string {
  const days = daysSince(iso);
  if (days === null) return 'No due date';
  if (days <= 0) return 'Today';
  return days === 1 ? '1 day' : `${String(days)} days`;
}

function whenLabel(iso: string): string {
  const days = daysSince(iso);
  if (days === null) return '';
  if (days <= 0) return 'Today';
  return days === 1 ? 'Yesterday' : `${String(days)} days ago`;
}

const ACTION_STYLE: Record<string, { readonly icon: IconName; readonly tone: AccentTone }> = {
  'finance.invoice.new': { icon: 'tray', tone: 'info' },
  'finance.bill.new': { icon: 'arrows-down-up', tone: 'warning' },
  'finance.journal.new': { icon: 'kanban', tone: 'teal' },
  'platform.report.new': { icon: 'chart-bar', tone: 'success' },
  'platform.party.new': { icon: 'users-three', tone: 'neutral' },
};

/* ==========================================================================
   THE PERIOD CONTROL
   ========================================================================== */

/**
 * The windows the control offers, as a REQUEST to the server.
 *
 * Each is computed from today's date and sent as from/to; the server decides
 * what it actually reads and says so, and the pill renders that answer. So
 * the option list is a convenience, not an authority — a caller who edits the
 * URL gets whatever the server resolves, and the pill still tells the truth.
 */
const PERIODS = [
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'last_3_months', label: 'Last 3 months' },
  { key: 'last_12_months', label: 'Last 12 months' },
] as const;

type PeriodKey = (typeof PERIODS)[number]['key'];

const iso = (date: Date): string => date.toISOString().slice(0, 10);

function windowFor(key: PeriodKey, now = new Date()): { from: string; to: string } {
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth();
  const today = new Date(Date.UTC(year, month, now.getUTCDate()));
  switch (key) {
    case 'last_month':
      return {
        from: iso(new Date(Date.UTC(year, month - 1, 1))),
        // Day zero of this month is the last day of the previous one.
        to: iso(new Date(Date.UTC(year, month, 0))),
      };
    case 'last_3_months':
      return { from: iso(new Date(Date.UTC(year, month - 2, 1))), to: iso(today) };
    case 'last_12_months':
      return { from: iso(new Date(Date.UTC(year, month - 11, 1))), to: iso(today) };
    default:
      return { from: iso(new Date(Date.UTC(year, month, 1))), to: iso(today) };
  }
}

const DAY_MONTH_YEAR = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});

/** `2026-09-01` → `01 Sep 2026`. Presentation of a date the server chose. */
function readableDay(value: string): string {
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return Number.isNaN(parsed.getTime()) ? value : DAY_MONTH_YEAR.format(parsed);
}

/* ==========================================================================
   COMPOSING A MODULE SNAPSHOT FROM WHAT THE SERVER SENT
   ========================================================================== */

/**
 * A card is DIMENSIONED when the server's own answer is already a breakdown —
 * one point per status, say. Those become the donut; the rest become figures.
 *
 * DERIVED FROM THE DATA, NOT FROM A LIST OF MODULE NAMES. The composition
 * rule has to hold for a module that does not exist yet, or Screen 00 acquires
 * a table somebody has to remember to edit. It reads the shape of the answer.
 */
function isDimensioned(card: MetricCard): boolean {
  const seen = new Set(card.points.map((point) => point.dimensionKey));
  seen.delete('');
  return seen.size > 1;
}

/** One figure in a snapshot's stat stack, refusal included. */
function statOf(card: MetricCard): SnapshotStat {
  if (card.unavailable !== undefined) {
    return { label: card.title, unavailable: card.unavailable.detail };
  }
  const change = changePercent(card);
  return {
    label: card.title,
    ...(cardValue(card) === undefined ? {} : { value: cardValue(card) }),
    ...(change === undefined ? {} : { changePercent: change }),
  };
}

const MONTH_LABEL = new Intl.DateTimeFormat('en-GB', { month: 'short', timeZone: 'UTC' });

/**
 * A grouped miniature from two of the module's own series.
 *
 * WHY TWO AND NOT ALL OF THEM. The reference plots revenue against expenses;
 * a quarter-width card that tried to plot five series would be a legend with a
 * chart attached. The first two series that have more than one period are
 * taken, in the order the server sent them, so the choice is the server's
 * ordering rather than a preference hidden here.
 *
 * ONLY WHERE THE PERIODS AGREE. Every series is read for the same window by
 * the same request, so their period keys line up; a period missing from one
 * series is plotted as the nought it is, not skipped.
 */
type Comparison =
  | {
      readonly kind: 'chart';
      readonly series: readonly { label: string; tone: SegmentTone }[];
      readonly groups: readonly MiniBarGroup[];
    }
  /** The pair exists; this window is too short to compare it against itself. */
  | { readonly kind: 'insufficient_history' }
  /** No pair at all — this module has nothing that could ever be plotted here. */
  | null;

function comparisonChart(cards: readonly MetricCard[]): Comparison {
  /*
   * AND IN THE SAME UNIT. Two series share one axis, so plotting a count
   * against a currency draws a bar of pence beside a bar of invoices and
   * invites the reader to compare their heights. The first plottable card
   * fixes the unit; the second must match it or there is no pair.
   */
  const candidates = cards.filter(
    (card) => card.unavailable === undefined && card.points.length > 0,
  );
  const [first] = candidates;
  if (first === undefined) return null;
  const plottable = candidates.filter((card) => card.unit === first.unit).slice(0, 2);
  if (plottable.length < 2) return null;

  const periods = [
    ...new Set(plottable.flatMap((card) => card.points.map((point) => point.periodStart))),
  ].sort();
  /*
   * THE PAIR IS REAL AND THE WINDOW IS TOO SHORT — which is a different answer
   * from "there is nothing here", and the card says so rather than dropping
   * its visual and stretching into a column of figures. One period cannot be
   * compared with itself, and a single bar or a flat line would both be a
   * claim about a trend nobody measured.
   */
  if (periods.length < 2) return { kind: 'insufficient_history' };

  const groups: MiniBarGroup[] = periods.slice(-6).map((period) => ({
    label: MONTH_LABEL.format(new Date(`${period}T00:00:00.000Z`)),
    values: plottable.map((card) =>
      card.points
        .filter((point) => point.periodStart === period)
        .reduce((total, point) => total + Math.abs(point.value), 0),
    ),
  }));

  return {
    kind: 'chart',
    series: plottable.map((card, index) => ({
      label: card.title,
      tone: CHART_SERIES[index % CHART_SERIES.length] ?? 'series1',
    })),
    groups,
  };
}

export function DashboardScreen({
  gateway,
  onOpen,
  initials,
}: {
  readonly gateway: DashboardGateway;
  readonly onOpen: (path: string) => void;
  /** The reader's own initials — Recent Activity is their own actions. */
  readonly initials: string;
}) {
  const [periodKey, setPeriodKey] = useState<PeriodKey>('this_month');
  const bridge = useShellBridge();
  // The window is a REQUEST. Recomputed only when the selection changes, so a
  // re-render cannot quietly ask for a different day than the one on screen.
  const requested = useMemo(() => windowFor(periodKey), [periodKey]);
  const fetcher = useCallback(() => gateway.dashboard(requested), [gateway, requested]);
  const { state, reload } = useAsync<Dashboard>(fetcher, [fetcher]);

  const unread = state.status === 'ready' ? state.value.notifications.unread : undefined;
  useEffect(() => {
    // The bell is in the shell on every page; the count arrives inside this
    // screen's own composed read model. This is the only place that knows it.
    if (unread !== undefined) bridge.reportUnread(unread);
  }, [bridge, unread]);

  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return <LoadingState title="Loading your dashboard" />;
  const dashboard = state.value;

  const { myWork, needsAttention, recentActivity, quickActions, moduleSnapshots } = dashboard;
  // Every snapshot is OF the same window, so each header says which one rather
  // than each carrying a selector that could disagree with the others.
  const periodLabel = PERIODS.find((option) => option.key === periodKey)?.label ?? 'This month';
  const workVisible = myWork.tasksAvailable || myWork.approvalsAvailable;
  const overdue =
    needsAttention === null ? 0 : needsAttention.overdueTasks + needsAttention.overdueApprovals;

  /*
   * EVERY SECTION, and `intelligence` was missing from this list.
   *
   * A caller entitled to Intelligence and nothing else was shown "Nothing here
   * yet" over a section the server had sent — the screen hiding an answer,
   * which is the one thing this file is written not to do. Found by a test
   * that rendered exactly that caller.
   */
  const nothingAtAll =
    dashboard.executiveSnapshot === null &&
    needsAttention === null &&
    dashboard.intelligence === null &&
    quickActions.length === 0 &&
    moduleSnapshots.length === 0 &&
    !workVisible &&
    recentActivity.items.length === 0;

  if (nothingAtAll) {
    return (
      <EmptyState
        icon="squares-four"
        title="Nothing here yet"
        description="You are signed in, but no modules or permissions have been assigned to you. An administrator can grant them."
      />
    );
  }

  return (
    <>
      {/*
        THE FILTER BAR. The reference puts the section's name on the left and
        the period controls on the right, above the KPI row rather than inside
        a card. The pill is NOT the selector's own state: it renders the window
        the server says it read, which differs whenever a request was narrowed.
      */}
      <div className="dashboard__filterbar">
        <div className="dashboard__filterbar-titles">
          <h2 className="dashboard__filterbar-title">Executive snapshot</h2>
          <span className="dashboard__filterbar-strap">
            Key figures across every module you can reach
          </span>
        </div>
        <div className="dashboard__filterbar-controls">
          <span className="dashboard__period" data-testid="dashboard-period">
            {`${readableDay(dashboard.period.from)} – ${readableDay(dashboard.period.to)}`}
          </span>
          <label className="dashboard__period-select">
            <span className="visually-hidden">Period</span>
            <select
              value={periodKey}
              onChange={(event) => {
                setPeriodKey(event.target.value as PeriodKey);
              }}
            >
              {PERIODS.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {dashboard.executiveSnapshot !== null && (
        /*
         * AS MANY COLUMNS AS THERE ARE CARDS, which is the designer's own rule:
         * `repeat(${visibleKpis.length}, minmax(0, 1fr))`. A caller entitled to
         * two metrics gets two slots of a six-slot row filled rather than two
         * half-width cards, and the count is exact — never `auto-fit` (§15).
         */
        <ExecutiveBand columns={dashboard.executiveSnapshot.cards.length}>
          {dashboard.executiveSnapshot.cards.map((card) => (
            <Kpi key={card.key} card={card} comparedTo={dashboard.period.comparedTo} />
          ))}
        </ExecutiveBand>
      )}

      {/*
        THE REFERENCE'S SECOND BAND: My Work, Needs Attention, Recent Activity
        and Quick Actions across one row. `auto-fit`, so a caller who has three
        of them gets three that fill the row rather than three and a hole.
      */}
      <DashboardBand>
        {workVisible && (
          <Card padding="compact">
            <SectionHeader
              icon="tray"
              tone="info"
              title="My work"
              subtitle="Assigned to you, from every module."
              {...(myWork.tasksAvailable
                ? { action: <ViewAll onOpen={onOpen} to="/tasks" /> }
                : {})}
            />
            <RowList>
              {myWork.tasksAvailable && (
                <CountRow
                  icon="tray"
                  tone="info"
                  count={myWork.tasks.length}
                  label="Tasks assigned to me"
                  onOpen={() => {
                    onOpen('/tasks');
                  }}
                />
              )}
              {myWork.approvalsAvailable && (
                <CountRow
                  icon="check-circle"
                  tone="success"
                  count={myWork.approvals.length}
                  label="Approvals pending"
                  onOpen={() => {
                    onOpen('/approvals');
                  }}
                />
              )}
              {needsAttention !== null && (
                <CountRow
                  icon="warning-circle"
                  tone="danger"
                  count={overdue}
                  label="Overdue items"
                />
              )}
            </RowList>
          </Card>
        )}

        {needsAttention !== null && (
          <Card padding="compact">
            <SectionHeader
              icon="warning-circle"
              tone="danger"
              title="Needs attention"
              subtitle="Past its due date and still open."
              {...(overdue > 0 ? { count: overdue } : {})}
            />
            {needsAttention.items.length === 0 ? (
              <EmptyState
                icon="check-circle"
                title="Nothing overdue"
                description="Nothing assigned to you has passed its due date."
              />
            ) : (
              <RowList>
                {needsAttention.items.map((item) => (
                  <AttentionRow
                    key={`${item.kind}-${item.id}`}
                    icon={item.kind === 'task' ? 'tray' : 'check-circle'}
                    tone="warning"
                    title={item.title}
                    chip={item.kind === 'task' ? 'Task' : 'Approval'}
                    age={ageLabel(item.dueAt)}
                  />
                ))}
              </RowList>
            )}
          </Card>
        )}

        <Card padding="compact">
          <SectionHeader
            icon="list"
            tone="neutral"
            title="Recent activity"
            /*
             * WHAT YOU DID — and the subtitle says so, because it is not the
             * tenant feed the reference draws. `record_activity` carries no
             * scope of its own, so a tenant-wide query would disclose the
             * existence of records the reader may not see. The authorised
             * cross-resource feed is a P3 capability.
             */
            subtitle="What you did, across every module."
          />
          {recentActivity.items.length === 0 ? (
            <EmptyState
              icon="list"
              title="No recent activity"
              description="Nothing you have done has been recorded yet."
            />
          ) : (
            <RowList>
              {recentActivity.items.map((item) => (
                <ActivityRow
                  key={item.id}
                  initials={initials}
                  title={activityLabel(item.activityType)}
                  chip={moduleOf(item.resource)}
                  when={whenLabel(item.occurredAt)}
                />
              ))}
            </RowList>
          )}
        </Card>

        {quickActions.length > 0 && (
          <Card padding="compact">
            <SectionHeader icon="lightning" tone="warning" title="Quick actions" />
            <ActionTileGrid>
              {quickActions.map((action) => {
                const style = ACTION_STYLE[action.key] ?? DEFAULT_STYLE;
                return (
                  <ActionTile
                    key={action.key}
                    icon={style.icon}
                    tone={style.tone}
                    label={action.label}
                    onSelect={() => {
                      onOpen(action.path);
                    }}
                  />
                );
              })}
            </ActionTileGrid>
          </Card>
        )}
      </DashboardBand>

      {/*
        A SNAPSHOT PER MODULE THE TENANT BOUGHT AND THIS CALLER CAN READ.
        The reference draws eight. Seven of those modules have no tables, no
        permissions and no declared metrics, so they contribute nothing — not
        an empty card, which would be the product claiming to have something it
        does not. Each appears the day it declares its first authorised metric.
      */}
      {(moduleSnapshots.length > 0 || dashboard.intelligence !== null) && (
        <DashboardBand band="snapshot">
          {moduleSnapshots.map((snapshot) => (
            <ModuleSnapshotCard key={snapshot.key} snapshot={snapshot} period={periodLabel} />
          ))}

          {dashboard.intelligence !== null && (
            <Card padding="compact">
              <SectionHeader
                icon="sparkle"
                tone="teal"
                title="Intelligence"
                subtitle="Reporting and analysis across the modules you can reach."
              />
              {/*
                NOTHING IS GENERATED WHEN THIS SCREEN LOADS (owner decision
                D-3). The reference draws four written insights; producing them
                would mean a model call on every dashboard view, metered and
                charged, asserting things about a tenant's data that nobody
                asked about. All three tiles are entry points: two to reporting
                surfaces that already exist, and one to the drawer, where a
                question is asked on purpose and answered under the asker's own
                entitlement, permission and record scope.
              */}
              <ActionTileGrid>
                <ActionTile
                  icon="chart-bar"
                  tone="info"
                  label="Reports"
                  onSelect={() => {
                    onOpen('/reports');
                  }}
                />
                <ActionTile
                  icon="trend-up"
                  tone="success"
                  label="Metrics"
                  onSelect={() => {
                    onOpen('/metrics');
                  }}
                />
                <ActionTile
                  icon="sparkle"
                  tone="teal"
                  label="Ask TESSMA AI"
                  onSelect={bridge.openAskAi}
                />
              </ActionTileGrid>
            </Card>
          )}
        </DashboardBand>
      )}
    </>
  );
}

/**
 * A MODULE'S WHOLE POSITION IN ONE CARD, composed from what the server sent.
 *
 * The reference's bottom band is eight of these and each is a single card:
 * figures on the left, a chart on the right. This screen used to render a row
 * of separate KPI cards per module, which threw away the thing the composition
 * is for.
 *
 * WHICH VISUAL IT GETS IS DECIDED BY THE DATA. A card whose answer is already
 * a breakdown becomes the donut; failing that, two series with more than one
 * period become the grouped miniature; failing that, there is no chart and the
 * figures take the card. No module is named here, so a module that ships its
 * first metric tomorrow gets the right treatment with no edit to this file.
 */
/**
 * **HOW MANY FIGURES A SNAPSHOT SHOWS, AND WHY IT IS THREE.**
 *
 * A dashboard snapshot is not a miniature of the module's report. The frozen
 * reference draws three figures beside one visual in a card of this height, and
 * that is not a stylistic preference: at the frozen anatomy — a label, a 20px
 * figure and its delta, on a 56px pitch — three is what the card's 186 CSS of
 * body actually holds. Finance declares seven, and rendering all seven is what
 * produced truncated labels, colliding figures and a visual with nothing left
 * to draw in.
 *
 * The three are not chosen here. `snapshotProviders` orders a module's figures
 * by the `executiveRank` the module itself declares, so Finance leads with
 * revenue, expenses and net profit because Finance says those are its headline
 * — and a module that declares no rank keeps its declared order. Nothing is
 * hidden from the reader: the rest are authorised figures with a surface of
 * their own on the module's overview.
 */
const SNAPSHOT_FIGURES = 3;

function ModuleSnapshotCard({
  snapshot,
  period,
}: {
  readonly snapshot: ModuleSnapshot;
  readonly period: string;
}) {
  const breakdown = snapshot.cards.find(
    (card) => card.unavailable === undefined && isDimensioned(card),
  );
  const scalars = snapshot.cards.filter((card) => card !== breakdown);
  const chart = breakdown === undefined ? comparisonChart(scalars) : null;
  const { icon, tone } = styleFor(snapshot.cards[0]?.key ?? '');

  const visual =
    breakdown !== undefined ? (
      /*
       * NO CAPTION INSIDE THE RING, the same decision the executive card made
       * next door and for the same reason, which a capture of this screen
       * showed: "Invoices by status" set in the middle of the donut wraps
       * across the arc and over the total it is meant to caption. The card
       * already carries the metric's name above it; it does not need saying
       * twice, least of all on top of the drawing.
       */
      <DonutWithLegend
        segments={segmentsOf(breakdown)}
        total={cardValue(breakdown) ?? '—'}
        label={breakdown.title}
      />
    ) : chart?.kind === 'chart' ? (
      <MiniBarChart
        label={`${chart.series.map((entry) => entry.label).join(' against ')}, by month`}
        caption={chart.series.map((entry) => entry.label).join(' vs ')}
        series={chart.series}
        groups={chart.groups}
      />
    ) : chart?.kind === 'insufficient_history' ? (
      <ChartUnavailable
        title="Not enough history to compare"
        detail="This window covers one period. Choose a wider range to see it against the months before it."
      />
    ) : undefined;

  return (
    <SnapshotCard
      icon={icon}
      tone={tone}
      title={snapshot.title}
      period={period}
      stats={scalars.slice(0, SNAPSHOT_FIGURES).map(statOf)}
      {...(visual === undefined ? {} : { visual })}
    />
  );
}

/** The reference's right-hand "View all →" on a section header. */
function ViewAll({ onOpen, to }: { readonly onOpen: (path: string) => void; readonly to: string }) {
  return (
    <button
      type="button"
      className="dashboard__view-all"
      onClick={() => {
        onOpen(to);
      }}
    >
      View all
    </button>
  );
}
